/**
 * Client-side logger for ADHD-focused applications
 * Reduces console noise in production while maintaining essential debugging
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4
}

export interface LogContext {
  userId?: string;
  component?: string;
  action?: string;
  duration?: number;
  [key: string]: any;
}

class ClientLogger {
  private level: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.level = this.isDevelopment ? LogLevel.TRACE : LogLevel.WARN;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.level;
  }

  private sanitizeContext(context?: LogContext): LogContext {
    if (!context) return {};
    
    const sanitized = { ...context };
    
    // Always remove sensitive data
    delete sanitized.password;
    delete sanitized.token;
    delete sanitized.apiKey;
    delete sanitized.secret;
    
    return sanitized;
  }

  private formatMessage(level: string, message: string, context?: LogContext): any[] {
    const sanitizedContext = this.sanitizeContext(context);
    const timestamp = new Date().toISOString();
    
    if (this.isDevelopment) {
      return [
        `%c[${level}] ${message}`,
        this.getConsoleStyle(level),
        {
          timestamp,
          ...sanitizedContext
        }
      ];
    } else {
      return [
        `[${level}] ${message}`,
        {
          timestamp,
          ...sanitizedContext
        }
      ];
    }
  }

  private getConsoleStyle(level: string): string {
    const styles = {
      ERROR: 'color: #ff4757; font-weight: bold;',
      WARN: 'color: #ffa502; font-weight: bold;',
      INFO: 'color: #3742fa; font-weight: bold;',
      DEBUG: 'color: #2f3542;',
      TRACE: 'color: #a4b0be;'
    };
    return styles[level as keyof typeof styles] || '';
  }

  error(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(...this.formatMessage('ERROR', message, context));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(...this.formatMessage('WARN', message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(...this.formatMessage('INFO', message, context));
    }
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(...this.formatMessage('DEBUG', message, context));
    }
  }

  trace(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.TRACE)) {
      console.log(...this.formatMessage('TRACE', message, context));
    }
  }

  // ADHD-specific logging methods
  adhdAction(action: string, context?: LogContext): void {
    this.info(`ADHD Action: ${action}`, {
      component: 'ADHD',
      action,
      ...context
    });
  }

  componentMount(componentName: string, context?: LogContext): void {
    this.debug(`Component mounted: ${componentName}`, {
      component: componentName,
      action: 'mount',
      ...context
    });
  }

  componentUnmount(componentName: string, context?: LogContext): void {
    this.debug(`Component unmounted: ${componentName}`, {
      component: componentName,
      action: 'unmount',
      ...context
    });
  }

  performance(operation: string, duration: number, context?: LogContext): void {
    const level = duration > 1000 ? LogLevel.WARN : LogLevel.DEBUG;
    if (this.shouldLog(level)) {
      const message = `Performance: ${operation} took ${duration}ms`;
      if (level === LogLevel.WARN) {
        this.warn(message, { operation, duration, slow: true, ...context });
      } else {
        this.debug(message, { operation, duration, ...context });
      }
    }
  }

  apiCall(endpoint: string, method: string, duration: number, success: boolean, context?: LogContext): void {
    const message = `API ${method} ${endpoint} ${success ? 'success' : 'failed'} (${duration}ms)`;
    if (success) {
      this.debug(message, { endpoint, method, duration, success, ...context });
    } else {
      this.warn(message, { endpoint, method, duration, success, ...context });
    }
  }

  // Replace console.log calls in components
  deprecatedConsoleLog(message: string, ...args: any[]): void {
    if (this.isDevelopment) {
      console.warn('🚨 Using console.log directly. Use logger instead:', message, ...args);
    }
  }
}

// Export singleton instance
export const logger = new ClientLogger();

// Development helper to replace console.log
if (process.env.NODE_ENV === 'development') {
  const originalConsoleLog = console.log;
  console.log = (...args: any[]) => {
    logger.deprecatedConsoleLog('Direct console.log call', ...args);
    originalConsoleLog(...args);
  };
}